import omit from 'lodash/omit';
import { Children, isValidElement, type CSSProperties, type ReactNode } from 'react';
import type { Components } from 'react-markdown';
import Markdown, { defaultUrlTransform } from 'react-markdown';

import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import remarkBreaks from 'remark-breaks';
import remarkGfm from 'remark-gfm';
import { oneLight } from 'react-syntax-highlighter/dist/esm/styles/prism';
import ChatMarkdownStreamdown from './chat-markdown-streamdown';
import { isChatStreamdownEnabled } from '../../lib/chat-render-path';
import { embedBareImageUrlsInMarkdown } from '../../lib/mcp-tool-utils';

/** Allow data/blob image URLs; default transform strips them. */
function chatMarkdownUrlTransform(url: string): string {
  const t = String(url ?? '').trim();
  if (!t) return t;
  if (/^data:image\//i.test(t)) return t;
  if (/^blob:/i.test(t)) return t;
  if (/^https?:\/\//i.test(t)) return t;
  return defaultUrlTransform(url);
}

/** hast may set URL props as string or string[] (e.g. multiple tokens). */
function normalizeUrlProp(value: unknown): string | undefined {
  if (value == null) return undefined;
  if (Array.isArray(value)) {
    const first = value.find((v) => v != null && String(v).trim() !== '');
    return first != null ? String(first) : undefined;
  }
  const s = String(value).trim();
  return s === '' ? undefined : s;
}

const INNER_HTML_LANGS = new Set(['html', 'htm', 'xml']);

/** True when trimmed content starts with a media-related tag (fenced block with no language). */
function isDomMediaSnippet(t: string): boolean {
  const s = t.trim();
  if (!s) return false;
  return /^<\s*(img|svg|picture|video|iframe)\b/i.test(s);
}

/** GFM tables need a blank line after a heading or they stay as pipe text. */
function ensureBlankLineBeforeGfmTables(markdown: string): string {
  return markdown.replace(/(^|\n)(#{1,6}[^\n]*)\n(\|[^\n]+\|)/g, '$1$2\n\n$3');
}

/**
 * Models often return `&lt;img ...&gt;` or wrap HTML in a single ```html fence — both were invisible:
 * entities don't parse as tags; fenced html is plain code text to remark, not real img nodes.
 */
function prepareChatMarkdownContent(content: string): string {
  let s = content;
  if (typeof document !== 'undefined' && /&lt;\s*(img|svg|picture)\b/i.test(s)) {
    const ta = document.createElement('textarea');
    ta.innerHTML = s;
    s = ta.value;
  }
  const trimmed = s.trim();
  const fence = /^```([a-zA-Z0-9_-]*)\r?\n([\s\S]*?)\r?\n```\s*$/u.exec(trimmed);
  if (fence && /<\s*(img|svg|picture)\b/i.test(fence[2])) {
    return embedBareImageUrlsInMarkdown(fence[2].trim());
  }
  return embedBareImageUrlsInMarkdown(ensureBlankLineBeforeGfmTables(s));
}

const MARKDOWN_PIPELINE = {
  remarkPlugins: [remarkGfm, remarkBreaks],
  remarkRehypeOptions: { allowDangerousHtml: true } as const,
  rehypePlugins: [rehypeRaw],
  urlTransform: chatMarkdownUrlTransform,
};

/** Marker column width in `ch` so short lists stay tight and 1000+ still fits. */
function getOrderedListMarkerWidthCh(start: unknown, children: ReactNode): number {
  const startNum = start != null && !Number.isNaN(Number(start)) ? Number(start) : 1;
  let itemCount = 0;
  let maxNum = startNum;

  Children.forEach(children, (child) => {
    if (!isValidElement(child)) return;
    itemCount += 1;
    const value = (child.props as { value?: unknown })?.value;
    if (value != null && !Number.isNaN(Number(value))) {
      maxNum = Math.max(maxNum, Number(value));
    }
  });

  if (itemCount > 0) {
    maxNum = Math.max(maxNum, startNum + itemCount - 1);
  }

  return Math.max(2, String(maxNum).length + 1);
}

export type ChatMarkdownProps = {
  content: string;
  className?: string;
  /** Show typewriter caret while reveal lags behind buffer. */
  isStreaming?: boolean;
  /** Streamdown remend / incomplete-block handling (defaults to isStreaming). */
  isStreamAnimating?: boolean;
};

function languageFromClassName(className: string | undefined): string | null {
  if (!className) return null;
  const match = /language-([\w+-]+)/.exec(className);
  return match ? match[1] : null;
}

const markdownComponents: Components = {
  img: (props) => {
    const { className, alt, src, srcSet, ...rest } = omit(props, ['node', 'children']);
    const srcNorm = normalizeUrlProp(src);
    const srcSetNorm = srcSet != null ? String(srcSet) : undefined;
    return (
      <img
        {...rest}
        src={srcNorm}
        srcSet={srcSetNorm}
        alt={alt != null ? String(alt) : ''}
        className={['inline-block max-w-full h-auto align-middle my-0.5', className]
          .filter(Boolean)
          .join(' ')}
        decoding="async"
        referrerPolicy="no-referrer"
      />
    );
  },
  a: (props) => {
    const { children } = props;
    const rest = omit(props, ['node', 'children']);
    return (
      <a
        {...rest}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: '#007bff',
          wordWrap: 'break-word',
          overflowWrap: 'break-word',
          wordBreak: 'break-word',
        }}
      >
        {children}
      </a>
    );
  },
  pre: ({ children }): ReactNode => children,
  code: (props) => {
    const { className, children } = props;
    const text = String(children).replace(/\n$/, '');
    const lang = languageFromClassName(className);
    const isInline = !className && !text.includes('\n');

    const renderHtmlMediaSnippet = (source: string) => (
      <div className="chat-markdown-html-snippet my-2 max-w-full rounded-lg border border-gray-200 bg-white p-2 overflow-x-auto">
        <Markdown {...MARKDOWN_PIPELINE} components={markdownComponents}>
          {source.trim()}
        </Markdown>
      </div>
    );

    if (isInline) {
      if (isDomMediaSnippet(text)) {
        return <span className="inline align-middle">{renderHtmlMediaSnippet(text)}</span>;
      }
      return (
        <code
          style={{
            background: '#f4f4f5',
            color: '#1f2937',
            padding: '2px 7px',
            borderRadius: '5px',
            border: '1px solid #e4e4e7',
            fontSize: '0.875em',
            letterSpacing: '0.02em',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          }}
        >
          {children}
        </code>
      );
    }

    const renderFencedBlockAsHtml =
      /<\s*(img|svg|picture)\b/i.test(text) &&
      ((lang != null && INNER_HTML_LANGS.has(lang)) || (!lang && isDomMediaSnippet(text)));

    if (renderFencedBlockAsHtml) {
      return renderHtmlMediaSnippet(text);
    }

    return (
      <SyntaxHighlighter
        language={lang || 'text'}
        style={oneLight}
        PreTag="pre"
        showLineNumbers={false}
        customStyle={{
          margin: '0.75em 0',
          padding: '14px 18px',
          paddingTop: '40px',
          borderRadius: '10px',
          fontSize: '13.5px',
          lineHeight: 1.65,
          letterSpacing: '0.02em',
          maxWidth: '100%',
          background: '#f7f7f8',
          border: '1px solid #e8e8ea',
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
          color: '#1e1e1e',
        }}
        codeTagProps={{
          style: {
            fontFamily:
              'ui-monospace, SFMono-Regular, Menlo, Monaco, "Cascadia Code", Consolas, monospace',
            letterSpacing: '0.02em',
          },
        }}
      >
        {text}
      </SyntaxHighlighter>
    );
  },
  table: ({ children }) => (
    <div className="chat-markdown-table-scroll my-3 max-w-full overflow-x-auto">
      <table className="min-w-full border-collapse border border-gray-300 text-sm">
        {children}
      </table>
    </div>
  ),
  thead: (props) => <thead className="bg-gray-100" {...omit(props, ['node'])} />,
  tbody: (props) => <tbody {...omit(props, ['node'])} />,
  tr: (props) => <tr className="even:bg-gray-50/50" {...omit(props, ['node'])} />,
  th: (props) => (
    <th
      className="border border-gray-300 px-3 py-2 text-left font-semibold text-gray-900"
      {...omit(props, ['node'])}
    />
  ),
  td: (props) => (
    <td
      className="border border-gray-300 px-3 py-2 align-top text-gray-800"
      {...omit(props, ['node'])}
    />
  ),
  h1: (props) => {
    const { children } = props;
    return (
      <h1
        className="text-xl font-semibold mt-3 mb-2 text-gray-900"
        {...omit(props, ['node', 'children'])}
      >
        {children}
      </h1>
    );
  },
  h2: (props) => {
    const { children } = props;
    return (
      <h2
        className="text-lg font-semibold mt-3 mb-2 text-gray-900"
        {...omit(props, ['node', 'children'])}
      >
        {children}
      </h2>
    );
  },
  h3: (props) => {
    const { children } = props;
    return (
      <h3
        className="text-base font-semibold mt-2 mb-1 text-gray-900"
        {...omit(props, ['node', 'children'])}
      >
        {children}
      </h3>
    );
  },
  h4: (props) => {
    const { children } = props;
    return (
      <h4
        className="text-sm font-semibold mt-2 mb-1 text-gray-900"
        {...omit(props, ['node', 'children'])}
      >
        {children}
      </h4>
    );
  },
  blockquote: (props) => (
    <blockquote
      className="border-l-4 border-gray-300 pl-3 my-2 text-gray-700 italic"
      {...omit(props, ['node'])}
    />
  ),
  p: (props) => <p className="my-2 first:mt-0 last:mb-0" {...omit(props, ['node'])} />,
  ul: (props) => <ul className="list-disc pl-5 my-2 space-y-1" {...omit(props, ['node'])} />,
  ol: ({ start, children, ...props }) => {
    const markerWidthCh = getOrderedListMarkerWidthCh(start, children);
    return (
      <ol
        {...omit(props, ['node', 'children'])}
        start={start}
        className="chat-markdown-ol my-2 space-y-1"
        style={{ '--ol-marker-ch': markerWidthCh } as CSSProperties}
      >
        {children}
      </ol>
    );
  },
};

function ChatMarkdown({
  content,
  className,
  isStreaming = false,
  isStreamAnimating,
}: ChatMarkdownProps) {
  if (!content && !isStreaming) return null;
  const prepared = content ? prepareChatMarkdownContent(content) : '';
  const streamAnimating = isStreamAnimating ?? isStreaming;

  if (isChatStreamdownEnabled()) {
    return (
      <ChatMarkdownStreamdown
        preparedContent={prepared}
        className={className}
        isStreamAnimating={streamAnimating}
        showCaret={isStreaming}
      />
    );
  }

  return (
    <div
      className={className}
      style={{
        overflowWrap: 'break-word',
        wordBreak: 'normal',
      }}
    >
      {prepared ? (
        <Markdown {...MARKDOWN_PIPELINE} components={markdownComponents}>
          {prepared}
        </Markdown>
      ) : null}
      {isStreaming ? <span className="typing-cursor">▍</span> : null}
    </div>
  );
}

ChatMarkdown.defaultProps = {
  className: '',
  isStreaming: false,
  isStreamAnimating: undefined,
};

export default ChatMarkdown;
